/**
 * useDashboardPromemoriaLog — F6 Home (PR-HOME-1).
 *
 * ADMIN senza Tier 2: nessun fetch, empty + link al Log (no useMfaStepUp).
 * DIETOLOGA: scope dietologaId == uid. Altri ruoli: studio-wide.
 * Import dinamico del service per non gonfiare il main bundle.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import type { PromemoriaLogRow } from '../../../services/promemoria';

export interface UseDashboardPromemoriaLogResult {
  items: PromemoriaLogRow[];
  loading: boolean;
  error: Error | null;
  /** ADMIN senza MFA Tier 2: non si tenta la lettura. */
  adminRequiresTier2: boolean;
  refresh: () => void;
}

const LOG_ROLES = new Set([
  'TITOLARE',
  'ADMIN',
  'SEGRETERIA',
  'DIETOLOGA',
]);

export function useDashboardPromemoriaLog(): UseDashboardPromemoriaLogResult {
  const { role, dietologaScopeUid, mfaTier } = useAuth();
  const [items, setItems] = useState<PromemoriaLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const mountedRef = useRef(true);

  const refresh = useCallback(() => {
    setRefreshCount((c) => c + 1);
  }, []);

  const isAdmin = role === 'ADMIN';
  const adminRequiresTier2 = isAdmin && mfaTier < 2;
  const skipFetch =
    !role ||
    !LOG_ROLES.has(role) ||
    adminRequiresTier2 ||
    (role === 'DIETOLOGA' && !dietologaScopeUid);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (skipFetch) return;

    let cancelled = false;

    const currentRole = role;
    const currentUid = dietologaScopeUid;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { fetchHomePromemoriaLog } = await import(
          '../../../services/promemoria-home'
        );
        const rows = await fetchHomePromemoriaLog({
          dietologaId:
            currentRole === 'DIETOLOGA' ? currentUid : null,
        });
        if (cancelled || !mountedRef.current) return;
        setItems(rows);
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        setItems([]);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled && mountedRef.current) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [skipFetch, role, dietologaScopeUid, refreshCount]);

  return {
    items: skipFetch ? [] : items,
    loading: skipFetch ? false : loading,
    error: skipFetch ? null : error,
    adminRequiresTier2,
    refresh,
  };
}
